import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { getInitialTab } from "@utils/getInitialTab";

// Import sub-components
import DietPlanOverviewHeader from "./DietPlanOverviewHeader";
import DietOverviewTabs from "./DietOverviewTabs";
import DietOverviewContent from "./DietOverviewContent";

const currentHour = new Date().getHours();

const DietPlanOverview = ({
  selectedMeal,
  selectedDay,
  selectedMeals,
  variant = "dashboard",
  showHeader = true,
  showClock = true,
  navigateTo = "/dashboard/recipe-detail",
  stateKey = "dashboard",
  tab,
  setTab,
  startDate = null,
  date = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const isCurrentMealTime = (category, type) => {
    const currentTab = getInitialTab();
    return currentTab.category === category && currentTab.type === type;
  };

  const handleCategoryChange = (newCategory) => {
    const currentTimeTab = getInitialTab();

    if (newCategory === currentTimeTab.category) {
      setTab(currentTimeTab);
    } else {
      let newType;

      if (newCategory === "meals") {
        if (currentHour >= 19) {
          newType = "dinner";
        } else if (currentHour >= 13) {
          newType = "lunch";
        } else {
          newType = "breakfast";
        }
      } else {
        if (currentHour >= 16) {
          newType = "evening";
        } else {
          newType = "afternoon";
        }
      }

      setTab({
        category: newCategory,
        type: newType,
      });
    }
  };

  const handleTypeChange = (newType) => {
    setTab((prev) => ({
      ...prev,
      type: newType,
    }));
  };

  const handleOpenRecipe = () => {
    const index = selectedMeals[selectedDay][tab.type];
    const newDate = new Date(date);
    navigate(navigateTo, {
      state: {
        mealType: tab.type,
        index: index,
        day: selectedDay,
        [stateKey]: true,
        tab: tab.type,
        tabCategory: tab?.category,
        startDate,
        dietPlanDate: newDate,
      },
    });
  };

  useEffect(() => {
    if (location.state?.tab) {
      setTab({
        category: location?.state?.category,
        type: location.state?.tab,
      });
    }
  }, [location?.state, setTab]);

  // Text helper functions
  const getPunchLine = () => {
    switch (tab.type) {
      case "breakfast":
        return t("dietPlanDashboard.mealListing.breakfastPunchLine");
      case "afternoon":
        return t("dietPlanDashboard.mealListing.afternoonSnackPunchLine");
      case "lunch":
        return t("dietPlanDashboard.mealListing.lunchPunchLine");
      case "evening":
        return t("dietPlanDashboard.mealListing.eveningSnackPunchLine");
      case "dinner":
        return t("dietPlanDashboard.mealListing.dinnerPunchLine");
      default:
        return "";
    }
  };

  const getMealTimeText = () => {
    if (!isCurrentMealTime(tab.category, tab.type)) return "";
    switch (tab.type) {
      case "breakfast":
        return t("dietPlanDashboard.mealListing.breakfastTime");
      case "afternoon":
      case "evening":
        return t("dietPlanDashboard.mealListing.snackTime");
      case "lunch":
        return t("dietPlanDashboard.mealListing.lunchTime");
      case "dinner":
        return t("dietPlanDashboard.mealListing.dinnerTime");
      default:
        return "";
    }
  };
  const currentTimeTab = getInitialTab();

  return (
    <div className={`${variant === "dashboard" ? "p-4 lg:p-6" : ""}`}>
      <DietPlanOverviewHeader
        variant={variant}
        showHeader={showHeader}
        showClock={showClock}
        selectedDay={selectedDay}
        getMealTimeText={getMealTimeText}
        getPunchLine={getPunchLine}
      />

      <DietOverviewTabs
        tab={tab}
        isCurrentMealTime={isCurrentMealTime}
        handleCategoryChange={handleCategoryChange}
        handleTypeChange={handleTypeChange}
        currentTimeTab={currentTimeTab}
      />

      {/* Add selectedMeal prop here */}
      <DietOverviewContent
        tab={tab}
        selectedMeal={selectedMeal} // ← Missing this!
        selectedDay={selectedDay} // ← Also add selectedDay if needed
        variant={variant}
        handleOpenRecipe={handleOpenRecipe}
      />
    </div>
  );
};

export default DietPlanOverview;
